/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React, { useEffect, useState } from "react"
import { Select } from "@duxton-web/ui"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { useTranslationsMap } from "common/utils/i18n"
import { BusinessType } from "types/models/menu"
import { ServiceType, ServiceTypeOptions } from "constants/feedback"
import { ExtraReviewsFilterConfig } from "types/api/menuFeedback.type"
import { compact, isEmpty } from "lodash"
import { getFeedbackDateRange } from "utils/feedback_helper"
import { CustomSelect } from "components/common"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { SEARCH_LIMIT } from "components/search/searchBarConstants"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useFetchMerchantAggregateQuery } from "queries/menuApi"
import { MerchantAggregate } from "types/api"
import useFeedbackStorelist from "./hooks/useFeedbackStoreList"

export interface FeedbackFiltersProps {
  onFetchReviews?(
    extras?: ExtraReviewsFilterConfig & { merchantIDs?: string[] },
  ): any
  isSingleStore?: boolean
  enableNonTxnReview?: boolean
}

const FeedbackTopFiltersNew: React.FC<FeedbackFiltersProps> = ({
  onFetchReviews = () => {},
  isSingleStore = false,
  enableNonTxnReview,
}) => {
  const {
    menu: {
      cityList: reduxCityList = [],
      merchantBusinessTypeList: reduxMerchantBusinessTypeList = [],
      merchantDeliverOptions: reduxMerchantDeliverOptions = [],
      outletV2,
      merchantGroupID,
    },
    feedback: { filterState },
  } = useSelector((state: AppState) => state)

  const {
    flags: { fetchMerchantAggregate: isFetchMerchantAggregateEnabled },
  } = useRtkQueryMigrationEndpointsFlag()

  const { data: merchantAggregateData } = useFetchMerchantAggregateQuery(
    {
      merchantGroupID,
      fields: [
        MerchantAggregate.cityID,
        MerchantAggregate.businessType,
        MerchantAggregate.deliverOption,
      ],
    },
    { skip: !isFetchMerchantAggregateEnabled || !merchantGroupID },
  )

  const cityList = isFetchMerchantAggregateEnabled
    ? merchantAggregateData?.cities ?? []
    : reduxCityList
  const merchantBusinessTypeList = isFetchMerchantAggregateEnabled
    ? merchantAggregateData?.businessTypes ?? []
    : reduxMerchantBusinessTypeList
  const merchantDeliverOptions = isFetchMerchantAggregateEnabled
    ? merchantAggregateData?.deliverOptionsOriginal ?? []
    : reduxMerchantDeliverOptions

  const textMap = useTranslationsMap({
    city: "menu.stores.table.header.city", // borrow content
    delivery: "feedback.filters.serviceType.delivery",
    deliveryAndSelfPickup: "feedback.filters.serviceType.deliveryAndSelfPickup",
    grabFood: "feedback.filters.businessType.grabFood",
    grabMart: "feedback.filters.businessType.grabMart",
    dineIn: "feedback.filters.serviceType.dineIn",
    allStores: "feedback.filters.allStores",
    all: "common.all",
    characterExceed: "common.characterExceed",
  })

  const businessTypeText: { [key: string]: string } = {
    [BusinessType.Food]: textMap.grabFood,
    [BusinessType.Shop]: textMap.grabMart,
  }
  // initialize these values as true in case the outlets array is not available
  let hasDelivery = true
  let hasSelfPickup = true
  let hasDineIn = true

  /**
   * for single store user, get serviceType option from outletV2 data
   * for multi store user, get serviceType option from merchant aggregate data
   */
  if (isSingleStore) {
    hasDelivery = !!outletV2?.supportDelivery
    hasSelfPickup = !!outletV2?.supportTakeaway
    hasDineIn = !!outletV2?.supportDineIn
  } else {
    hasDelivery = merchantDeliverOptions.some(({ deliverOption }) =>
      deliverOption.includes(ServiceTypeOptions.DELIVERY),
    )

    hasSelfPickup = merchantDeliverOptions.some(({ deliverOption }) =>
      deliverOption.includes(ServiceTypeOptions.TAKEAWAY),
    )

    hasDineIn = merchantDeliverOptions.some(({ deliverOption }) =>
      deliverOption.includes(ServiceTypeOptions.DINEIN),
    )
  }

  const serviceTypeText = {
    [ServiceType.DELIVERY]: hasSelfPickup
      ? textMap.deliveryAndSelfPickup
      : textMap.delivery,
    [ServiceType.DINEIN]: textMap.dineIn,
    [ServiceType.ALL]: textMap.all,
  }

  const availableServiceTypes = compact([
    (hasDelivery || hasDineIn) && ServiceType.DELIVERY,
    hasDineIn && ServiceType.DINEIN,
    enableNonTxnReview && ServiceType.ALL,
  ])

  const showServiceTypes =
    (outletV2?.businessType !== BusinessType.Shop ||
      filterState?.businessType !== BusinessType.Shop) &&
    !!availableServiceTypes.length

  const {
    handleFilter,
    filteredList,
    isFetchingOutletsSearch,
  } = useFeedbackStorelist()

  const handleCityChange = (cityID?: number) => {
    onFetchReviews({
      cityID,
    })
  }

  const handleBusinessTypeChange = (businessType: BusinessType) => {
    onFetchReviews({
      businessType,
      merchantIDs: undefined,
      serviceType: ServiceType.ALL,
    })
  }

  const handleServiceTypeChange = (serviceType: ServiceType) => {
    const { startDate, endDate } = getFeedbackDateRange()

    onFetchReviews({
      serviceType,
      merchantIDs: undefined,
      // clear date filter since there may be a difference in max allowed date limit between service types
      startDate,
      endDate,
    })
  }

  const [searchError, setSearchError] = useState("")

  const handleSearch = (value: string) => {
    if (value.length > SEARCH_LIMIT) {
      const errorMsg = textMap.characterExceed
        .replace("<current>", `${value.length}`)
        .replace("<limit>", `${SEARCH_LIMIT}`)
      setSearchError(errorMsg)
      return
    }

    setSearchError("")

    handleFilter({
      search: value,
      city: filterState?.cityID,
      businessType: filterState?.businessType,
      serviceType: filterState?.serviceType,
    })
  }

  const handleChange = (value: any) => {
    let merchantIDs = value

    if (value?.includes("ALL") || isEmpty(value)) {
      merchantIDs = undefined
    }

    onFetchReviews({
      merchantIDs,
    })
  }

  /**
   * for single store user, review can be fetched directly
   * for multi store user, reviews can be fetched only after the merchant aggregate data is fetched
   */
  useEffect(() => {
    if (isSingleStore || cityList?.length) {
      const { startDate, endDate } = getFeedbackDateRange()
      onFetchReviews({
        cityID: cityList?.[0]?.cityID,
        businessType:
          merchantBusinessTypeList?.[0]?.businessType ?? BusinessType.Food,
        serviceType: enableNonTxnReview
          ? ServiceType.ALL
          : ServiceType.DELIVERY,
        startDate,
        endDate,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityList?.length, isSingleStore])

  return (
    <div
      css={css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "16px",
      })}
    >
      {!isSingleStore && (
        <Select
          style={{
            minWidth: 160,
          }}
          placeholder={textMap.city}
          onChange={handleCityChange}
          data-testid="feedback-city-selector"
          value={filterState?.cityID}
        >
          {cityList?.map(({ cityID, cityName }) => (
            <Select.Option key={cityID} value={cityID}>
              {cityName}
            </Select.Option>
          ))}
        </Select>
      )}

      {!isSingleStore && (
        <Select
          style={{
            minWidth: 160,
          }}
          onChange={handleBusinessTypeChange}
          data-testid="feedback-business-type-selector"
          value={filterState?.businessType}
        >
          {merchantBusinessTypeList.map(({ businessType }) => (
            <Select.Option value={businessType} key={businessType}>
              {businessTypeText[businessType]}
            </Select.Option>
          ))}
        </Select>
      )}

      {showServiceTypes && (
        <Select
          style={{
            minWidth: 150,
          }}
          onChange={handleServiceTypeChange}
          data-testid="feedback-service-type-selector"
          value={filterState?.serviceType ?? enableNonTxnReview}
        >
          {availableServiceTypes.map(st => (
            <Select.Option value={st} key={st}>
              {serviceTypeText[st]}
            </Select.Option>
          ))}
        </Select>
      )}

      {!isSingleStore && (
        <CustomSelect
          css={css`
            .dui-select-selector {
              min-width: 200px;
            }
          `}
          data-testid="input-store"
          options={[
            {
              label: textMap.allStores,
              value: "ALL",
              disabled: !filterState?.merchantIDs?.length,
            },
            ...filteredList.map(item => ({
              label: item.merchantName,
              value: item.merchantID,
            })),
          ]}
          searchable
          searchDelayMs={DEFAULT_DEBOUNCE_WAIT_TIME}
          loading={isFetchingOutletsSearch}
          onSearch={handleSearch}
          filterOption={false}
          customDropdownStyle={{ width: 400 }}
          onChange={handleChange}
          paginationProps={{ showPagination: false }}
          mode="multiple"
          placeholder={textMap.allStores}
          value={filterState?.merchantIDs}
          error={searchError}
        />
      )}
    </div>
  )
}

export default FeedbackTopFiltersNew
